<template>
  <div class="message-box position-relative d-flex align-items-center d-flex flex-column">
    <b-form-textarea
      ref="messageBoxInput"
      v-model="message"
      trim
      id="message-box-input"
      class="regular-input message-type-box m-0 px-3 py-2"
      :rows="2"
      :placeholder="placeholder"
      :disabled="disabled"
      @keydown="handleKeyPress"
      @input="handleChangedMessage"
      style="height:80px; font-size: 16px;"
    ></b-form-textarea>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import { debounce } from "lodash-es";
import Fuse from "fuse.js";
import { EventBus } from "@/events/eventBus";

export default {
  name: "MessageBox",
  props: {
    value: String,
    placeholder: String,
    disabled: Boolean,
  },
  data() {
    return {
      message: "",
      showQuickReply: false
    };
  },
  computed: {
    ...mapGetters("conversations", ["conversationMessages"]),
    ...mapGetters("agency", ["agencySettings", "currentAgencyId"]),
    ...mapGetters("user", ["userTemplates"]),
    ...mapGetters("persist", ["lastQuickReply"]),
    autosuggestEnabled() {
      return this.agencySettings && this.agencySettings.enableAutosuggest == "true";
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue !== this.message) {
          this.message = newValue;
        }
      },
    },
  },
  created() {
    const self = this;
    setTimeout(function() {
      self.focusTextarea();
    }, 750);
    EventBus.$on('closed-quick-reply-suggestions', () => {
      this.showQuickReply = false;
    });
    EventBus.$on('quick-reply-suggestions-selected', (template) => {
      const { text, value } = template;
      this.$emit("input", text);
      this.message = text;
      this.$store.commit('persist/setLastQuickReply', {
        agencyId: this.currentAgencyId,
        templateId: value,
      });
      setTimeout(() => {
        this.$emit("send-message");
        this.showQuickReply = false;
        this.$nextTick(() => {
          this.focusTextarea();
        });
      }, 100);
    });
  },
  methods: {
    handleKeyPress(e) {
      const { key, shiftKey, ctrlKey, altKey, metaKey } = e;
      if (this.showQuickReply) {
        if (key === "ArrowDown") {
          EventBus.$emit('quick-reply-suggestions-keydown', key);
        } else if (key === "ArrowUp") {
          EventBus.$emit('quick-reply-suggestions-keydown', key);
        } else if (key === 'Enter' && !shiftKey && !ctrlKey && !altKey && !metaKey) {
          e.preventDefault();
          EventBus.$emit('quick-reply-suggestions-keydown', key);
        } else if (key === "Escape") {
          EventBus.$emit('quick-reply-suggestions-keydown', key);
        } else if (key === 'Enter' && (shiftKey || ctrlKey || altKey || metaKey)) {
          const textarea = e.target;
          const start = textarea.selectionStart; 
          const end = textarea.selectionEnd;
          const value = textarea.value;
          const newValue = value.substring(0, start) + "\n" + value.substring(end);
          this.handleChangedMessage(newValue);
          textarea.value = newValue;
          textarea.setSelectionRange(start + 1, start + 1);
          e.preventDefault();
        }
      } else {
        if (key === 'Enter' && !shiftKey && !ctrlKey && !altKey && !metaKey) {
          this.$nextTick(() => {
            if (this.message && this.message.trim() !== "") {
              this.$emit("send-message");
              this.message = "";
            }
          });
        } else if (key === 'Enter' && (shiftKey || ctrlKey || altKey || metaKey)) {
          e.preventDefault();
          const textarea = e.target;
          const start = textarea.selectionStart; 
          const end = textarea.selectionEnd;
          const value = textarea.value;
          const newValue = value.substring(0, start) + "\n" + value.substring(end);
          textarea.value = newValue;
          textarea.setSelectionRange(start + 1, start + 1);
          this.handleChangedMessage(newValue);
          e.preventDefault();
        }
      }
    },
    focusTextarea() {
      if (this.$refs.messageBoxInput) {
        this.$refs.messageBoxInput.focus();
      }
    },
    getLastWord(text) {
      const words = text.split(/\s+/);
      return words[words.length - 1];
    },
    debouncedFetchSuggestions: debounce(function () {
      const word = this.getLastWord(this.message);
      if (!word) {
        return;
      }

      const suggestions = this.findAutoSuggestion(this.message);
      this.$nextTick(() => {
        const pos = this.getCaretCoordinatesForLastWord();
        if (!(pos.x === 0 && pos.y === 0)) {
          EventBus.$emit('show-quick-reply-suggestions', {
            x: pos.x,
            y: pos.y - 60 * suggestions.length - 10,
            suggestions,
          });
          this.showQuickReply = suggestions.length > 0;
        }
      });
    }, 300),
    getCaretCoordinatesForLastWord() {
      if (this.$refs.messageBoxInput) {
        const el = this.$refs.messageBoxInput.$el;
        const position = el.selectionStart;
        if (!el) return null;

        const div = document.createElement('div');
        const style = getComputedStyle(el);
        for (const prop of style) div.style[prop] = style[prop];
        div.style.position = 'absolute';
        div.style.visibility = 'hidden';
        div.style.whiteSpace = 'pre-wrap';
        div.style.wordWrap = 'break-word';
        div.style.width = el.offsetWidth + 'px';
        div.style.height = el.offsetHeight + 'px';

        const beforeText = el.value.substring(0, position);
        const span = document.createElement('span');
        span.textContent = '\u200b';
        div.textContent = beforeText;
        div.appendChild(span);
        document.body.appendChild(div);

        const { offsetLeft: x, offsetTop: y } = span;
        const rect = el.getBoundingClientRect();
        document.body.removeChild(div);

        return {
          x: rect.left + x,
          y: rect.top + y + window.scrollY,
        };
      }
      return { x: 0, y: 0 };
    },
    handleChangedMessage(changedMessage) {
      this.$emit("input", changedMessage);
      this.message = changedMessage;
      if (changedMessage && changedMessage.trim() !== "") {
        this.debouncedFetchSuggestions();
      } else {
        EventBus.$emit('show-quick-reply-suggestions', {
          x: 0,
          y: 0,
          suggestions: [],
        });
        this.showQuickReply = false;
      }
    },
    findAutoSuggestion(changedMessage) {
      if (changedMessage && changedMessage.trim() !== "") {
        const lastQuickReply = this.lastQuickReply[this.currentAgencyId];
        const fuse = new Fuse(this.userTemplates, {
          keys: ["text", "label"],
          includeScore: true,
          threshold: 0.4,
          ignoreLocation: true
        });

        const results = fuse.search(changedMessage);
        const goodMatches = results.sort((a, b) => a.score - b.score).map(item => item.item);
        if (lastQuickReply) {
          const templateIndex = goodMatches.findIndex(item => item.value === lastQuickReply);
          if (templateIndex >= 0) {
            const template = {...goodMatches[templateIndex]};
            goodMatches.splice(templateIndex, 1);
            goodMatches.unshift(template);
          }

        }
        return [...goodMatches].splice(0, 5);
      }
      return [];
    },
  },
  beforeDestroy() {
    EventBus.$off('closed-quick-reply-suggestions');
    EventBus.$off('quick-reply-suggestions-selected');
  },
};
</script>

<style lang="scss" scoped>
.message-box {
  .matched-message-popover-trigger {
    width: 100%;
    height: 0;
    background-color: transparent;
    border: none;
  }
	&-list {
		position: absolute;
		z-index: 2;
		overflow: auto;
		min-width: 250px;
		max-height: 150px;
		margin: 0;
		margin-top: 5px;
		padding: 0;
		border: 1px solid #eee;
		list-style: none;
		border-radius: 4px;
		background-color: #fff;
		box-shadow: 0 5px 25px rgba(0, 0, 0, 0.05);

    textarea {
      font-size: 16px !important;
    }
	}

  textarea {
    border-radius: 1.5rem;
    resize: none;
  }
}
</style>